// EXPERIMENTAL — scan a photographed scorecard to prefill this shooter's entry
// form. Disabled by default; shown only when the parent decides the feature is
// on (build env REACT_APP_ENABLE_SCORECARD_OCR=true, or localStorage
// scorecardOcr="on" for on-device testing). The server endpoint is disabled by
// default too and returns 501 until the OCR engine is implemented — this UI
// degrades to a friendly "not turned on yet" message in that case.
//
// Flow: pick/take a photo -> POST to /matches/:id/scorecard-ocr -> onApply()
// maps the parsed stages onto the form. The user still reviews every value and
// presses Save; nothing is written straight from the scan.

import { useRef, useState } from "react";
import axios from "axios";

const ScorecardScan = ({ api, matchId, shooterId, onApply }) => {
  const inputRef = useRef(null);
  const [phase, setPhase] = useState("idle"); // idle | reading | done | error
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState(null);

  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = ""; // let the same file be re-picked
    if (!f) return;
    setPreview(URL.createObjectURL(f));
    setPhase("reading");
    setMessage("");
    try {
      const token = localStorage.getItem("token");
      const form = new FormData();
      form.append("file", f);
      const q = shooterId ? `?shooter_id=${encodeURIComponent(shooterId)}` : "";
      const res = await axios.post(
        `${api}/matches/${matchId}/scorecard-ocr${q}`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const filled = (onApply && onApply(res.data)) || 0;
      setPhase("done");
      setMessage(
        filled > 0
          ? `Filled ${filled} stage${filled === 1 ? "" : "s"} from the scan. Check every value, then Save.`
          : "Scan read, but nothing matched this match's stages. Enter the scores below."
      );
    } catch (err) {
      setPhase("error");
      const code = err && err.response && err.response.status;
      if (code === 501) {
        setMessage("Scanning isn't turned on yet — this is a preview of the flow.");
      } else if (code === 403) {
        setMessage("You don't have permission to enter scores for this match.");
      } else {
        const detail = err && err.response && err.response.data && err.response.data.detail;
        setMessage(detail || "Couldn't read that scorecard. Try a straight-on, well-lit photo.");
      }
    }
  };

  const disabled = !shooterId;

  return (
    <div className="card card-pad mb-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="section-title">Scan a scorecard</h3>
            <span className="badge-amber">Beta</span>
          </div>
          <p className="page-subtitle">
            Take a photo of the paper scorecard and we'll fill in the stages for
            you to review.
          </p>
        </div>
        {preview && (
          <img
            src={preview}
            alt="Scorecard preview"
            className="h-14 w-14 shrink-0 rounded-lg object-cover ring-1 ring-ink-200"
          />
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onFile}
        className="hidden"
      />

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={() => inputRef.current && inputRef.current.click()}
          disabled={disabled || phase === "reading"}
          className="btn-secondary"
        >
          {phase === "reading"
            ? "Reading…"
            : preview
            ? "Scan another"
            : "Take / upload photo"}
        </button>
        {disabled && (
          <span className="text-sm text-ink-500">Select a shooter first.</span>
        )}
      </div>

      {message && phase === "done" && (
        <div className="alert-success mt-3">{message}</div>
      )}
      {message && phase === "error" && (
        <div className="alert-info mt-3">{message}</div>
      )}
    </div>
  );
};

export default ScorecardScan;
